import React, { useMemo } from 'react';
import { GameValidator } from '@colonist/game-mechanic-validators';
import { ActionState, PlayerColor, TradeResponseStatus, TurnState } from '@colonist/game-states';
import { ArrayUtils } from '@colonist/utils';
import { LobbyImage } from '@colonist/types';
import { UIGameCurrentStatus } from './UIGameCurrentStatus.tsx';
import { useGameStore } from '../../store/GameStoreContext.ts';
import { usePlayerValidators } from '../../store/selectors/usePlayerValidators.ts';
import { useCurrentStateValidator } from '../../store/selectors/useCurrentStateValidator.ts';
export const UIGameCurrentStatusContainer = ({ className })=>{
    const gameState = useGameStore((state)=>state.gameState);
    const userStates = useGameStore((state)=>state.gameUserStates.userStates);
    const currentStateValidator = useCurrentStateValidator();
    const myColor = useGameStore((state)=>state.myColor);
    const { getPlayerValidator } = usePlayerValidators();
    const translationKeyContext = useGameStore((state)=>state.gameMode.translationKeyContext);
    const { amITakingAction, playersTakingAction } = useMemo(()=>{
        const gameValidator = new GameValidator(gameState, userStates);
        const playersTakingActionLocal = gameValidator.getPlayersTakingAction();
        const amITakingActionLocal = playersTakingActionLocal.includes(myColor);
        return {
            amITakingAction: amITakingActionLocal,
            playersTakingAction: playersTakingActionLocal
        };
    }, [
        gameState,
        userStates,
        myColor
    ]);
    const getPlayerDisplayUsername = (playersTakingActionLocal)=>{
        if (playersTakingActionLocal.length == 0) {
            const currentTurnPlayer = getPlayerValidator(gameState.currentState.currentTurnPlayerColor);
            return currentTurnPlayer.username;
        }
        if (playersTakingActionLocal.length == 1) {
            const playerState = getPlayerValidator(playersTakingActionLocal[0]);
            return playerState.username;
        }
        return 'Players';
    };
    const wasATradeAccepted = ()=>{
        for (const activeTradeState of Object.values(gameState.tradeState.activeOffers)){
            if (activeTradeState == undefined) continue;
            for (const player of Object.keys(activeTradeState.playerResponses)){
                const playerColor = Number(player);
                if (activeTradeState.playerResponses[playerColor] == TradeResponseStatus.Accepted) return true;
            }
        }
        return false;
    };
    const { playerDisplayIcon, playerDisplayColor } = useMemo(()=>{
        const amICurrentTurnPlayer = gameState.currentState.currentTurnPlayerColor == myColor;
        const wouldTradesAffectCurrentStateDisplay = amICurrentTurnPlayer && gameState.currentState.turnState == TurnState.Turn;
        const currentTurnPlayerColor = gameState.currentState.currentTurnPlayerColor;
        const currentTurnPlayer = getPlayerValidator(currentTurnPlayerColor);
        if (wouldTradesAffectCurrentStateDisplay) {
            const wasATradeAcceptedLocal = wasATradeAccepted();
            if (wasATradeAcceptedLocal) {
                return {
                    playerDisplayIcon: currentTurnPlayer.displayIcon,
                    playerDisplayColor: currentTurnPlayerColor
                };
            }
            const otherPlayersTrading = [
                ...playersTakingAction
            ];
            ArrayUtils.removeElementFromArray(otherPlayersTrading, currentTurnPlayerColor);
            if (otherPlayersTrading.length == 0) {
                return {
                    playerDisplayIcon: currentTurnPlayer.displayIcon,
                    playerDisplayColor: currentTurnPlayerColor
                };
            }
            if (otherPlayersTrading.length == 1) {
                const otherTradingPlayer = getPlayerValidator(otherPlayersTrading[0]);
                return {
                    playerDisplayIcon: otherTradingPlayer.displayIcon,
                    playerDisplayColor: otherTradingPlayer.color
                };
            }
            return {
                playerDisplayIcon: LobbyImage.Players,
                playerDisplayColor: PlayerColor.None
            };
        }
        if (playersTakingAction.length == 0) {
            return {
                playerDisplayIcon: currentTurnPlayer.displayIcon,
                playerDisplayColor: currentTurnPlayerColor
            };
        }
        if (playersTakingAction.length == 1) {
            const otherPlayer = getPlayerValidator(playersTakingAction[0]);
            return {
                playerDisplayIcon: otherPlayer.displayIcon,
                playerDisplayColor: otherPlayer.color
            };
        }
        if (amITakingAction) {
            const myPlayer = getPlayerValidator(myColor);
            return {
                playerDisplayIcon: myPlayer.displayIcon,
                playerDisplayColor: myPlayer.color
            };
        }
        return {
            playerDisplayIcon: LobbyImage.Players,
            playerDisplayColor: PlayerColor.None
        };
    }, [
        gameState,
        myColor,
        playersTakingAction,
        amITakingAction
    ]);
    const playerColorOfActionStateToDisplay = useMemo(()=>{
        if (currentStateValidator.currentTurnPlayers.includes(myColor)) return myColor;
        return currentStateValidator.currentTurnPlayers[0];
    }, [
        currentStateValidator,
        myColor
    ]);
    const currentStatusMessage = useMemo(()=>{
        const turnState = gameState.currentState.turnState;
        const actionState = currentStateValidator.getPlayerActionState(playerColorOfActionStateToDisplay);
        const isActionStateNone = actionState == ActionState.None;
        const playerDisplayUsername = getPlayerDisplayUsername(playersTakingAction);
        if (!isActionStateNone) {
            const currentStateMessageInput = {
                type: 'actionState',
                currentState: gameState.currentState,
                actionState: actionState,
                playersTakingAction: playersTakingAction,
                myColor: myColor,
                translationKeyContext: translationKeyContext,
                displayUsername: playerDisplayUsername
            };
            return currentStateMessageInput;
        }
        let turnStateMessageCalculationInput;
        if (turnState == TurnState.Turn) {
            const otherPlayersTrading = [
                ...playersTakingAction
            ];
            ArrayUtils.removeElementFromArray(otherPlayersTrading, gameState.currentState.currentTurnPlayerColor);
            const otherPlayerDisplayName = getPlayerDisplayUsername(otherPlayersTrading);
            turnStateMessageCalculationInput = {
                amITakingAction: amITakingAction,
                type: 'turnStateTurn',
                currentState: gameState.currentState,
                playersTakingAction: playersTakingAction,
                playerDisplayUsername: playerDisplayUsername,
                numberOfPlayersTakingAction: playersTakingAction.length,
                arePlayersTrading: Object.keys(gameState.tradeState.activeOffers).length > 0,
                myColor: myColor,
                wasATradeAccepted: wasATradeAccepted(),
                otherPlayerDisplayName: otherPlayerDisplayName
            };
        } else {
            turnStateMessageCalculationInput = {
                amITakingAction: amITakingAction,
                type: 'turnStateDefault',
                currentState: gameState.currentState,
                playerDisplayUsername: playerDisplayUsername
            };
        }
        return turnStateMessageCalculationInput;
    }, [
        amITakingAction,
        gameState,
        playersTakingAction,
        myColor,
        translationKeyContext,
        playerColorOfActionStateToDisplay
    ]);
    return /*#__PURE__*/ React.createElement(UIGameCurrentStatus, {
        className: className,
        currentStateMessageInput: currentStatusMessage,
        playerDisplayIcon: playerDisplayIcon,
        playerDisplayColor: playerDisplayColor
    });
};
